import { forwardRef, useState, useMemo } from "react"
import { Textarea } from "@f2e/ui/components/base/textarea"
import { cn } from "@f2e/ui/lib/utils"
import { AIButton } from "./AIButton"

type TextareaProps = React.ComponentProps<typeof Textarea>

export interface AITextareaProps extends TextareaProps {
  className?: string
  defaultValue: string
  aiTooltip?: string
  aiLoadingTooltip?: string
  withInstruction?: boolean
  isLoading?: boolean
  onGenerate?: (instruction?: string) => void
  onCancel?: () => void
}

const AITextarea = forwardRef<HTMLTextAreaElement, AITextareaProps>(
  (
    {
      className,
      defaultValue,
      aiTooltip,
      aiLoadingTooltip,
      withInstruction,
      isLoading,
      onGenerate,
      onCancel,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState<string>()

    useMemo(() => {
      setInputValue(defaultValue)
    }, [defaultValue])

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputValue(e.target.value)
      props.onChange?.(e)
    }

    const handleGenerate = (instruction?: string) => {
      onGenerate?.(instruction)
    }

    const handleCancelGenerate = () => {
      setInputValue("")
      onCancel?.()
    }

    return (
      <div className={cn("relative flex items-center gap-2", className)}>
        <Textarea
          {...props}
          ref={ref}
          className="relative pr-10"
          value={inputValue}
          onChange={handleChange}
        />
        <AIButton
          className="absolute top-[2px] right-[8px]"
          tooltip={aiTooltip}
          loadingTooltip={aiLoadingTooltip}
          withInstruction={withInstruction}
          isGenerating={isLoading}
          onGenerate={handleGenerate}
          onCancel={handleCancelGenerate}
        />
      </div>
    )
  }
)

AITextarea.displayName = "AITextarea"

export default AITextarea
